<script lang="ts" context="module">
  const MAX_PUBLISHED_DAYS_AGO = 10;
</script>

<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import dayjs from 'dayjs';
  import { ResponsiveVars, TwistBox, TwistStack } from '@twist/core';
  import { cx } from '@emotion/css';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import Container from '$components/News/Container.svelte';
  import NavigateBackLink from '$components/Navigation/NavigateBackLink.svelte';
  import ApplyButton from './ApplyButton.svelte';
  import JobLocation from './JobLocation.svelte';
  import JobPublicationDate, { publishedDaysAgoFrom } from './JobPublicationDate.svelte';
  import BUMarquee from './BUMarquee.svelte';
  import type { JobDetails } from '$lib/careers/types';
  import HorizontalBuLogo, { Theme } from '$components/Logos/HorizontalBULogo.svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import HeadingSizeDecorator from '$components/Common/HeadingSizeDecorator.svelte';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';

  export let job: JobDetails;

  const dispatch = createEventDispatcher();

  $: publishedDaysAgo = job.publicationDate ? publishedDaysAgoFrom(job.publicationDate) : undefined;

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);

  $: formattedPublicationDate =
    job.publicationDate && dayjs(job.publicationDate).format('MM/DD/YY');

  const responsiveVars = new ResponsiveVars()
    .addFluid('h1-font-size', toFluidVarInput(28, 72))
    .addFluid('bu-logo-height', toFluidVarInput(30, 34))
    .addFluid('floating-asset-width', toFluidVarInput(150, 300))
    .addFluid('floating-asset-right', toFluidVarInput(40, 158))
    .addFluid('floating-asset-top', toFluidVarInput(80, 55))
    .addFluid('id-font-size', toFluidVarInput(14, 14))
    .addFluid('gap', toFluidVarInput(8, 8))
    .toCSS();
</script>

<div class={cx('hero', responsiveVars)}>
  <TwistBox paddingTop={toFluidVarInput(90, 160)} paddingBottom={toFluidVarInput(13, 20)}>
    <Container>
      <TwistBox marginTop={toFluidVarInput(24, 0)} marginBottom={toFluidVarInput(60, 60)}>
        <NavigateBackLink to="/careers/jobs" text="SEE ALL JOBS" />
      </TwistBox>

      <TwistBox marginBottom={toFluidVarInput(18, 18)}>
        <div class="bu-logo">
          <HorizontalBuLogo businessUnit={job.bu} theme={Theme.LIGHT_ON_DARK} />
        </div>
      </TwistBox>

      <TwistBox marginBottom={toFluidVarInput(90, 45)}>
        <TwistStack align="flex-end" justify="space-between" wrap="nowrap">
          <h1>
            {#if brandReskinEnabled}
              <HeadingSizeDecorator size="h1">
                {job.title}
              </HeadingSizeDecorator>
            {:else}
              {job.title}
            {/if}
          </h1>
          <TwistBox display={{ min: 'none', lg: 'block' }}>
            <ApplyButton on:click={() => dispatch('apply-clicked')} />
          </TwistBox>
        </TwistStack>
      </TwistBox>

      <TwistBox marginBottom={toFluidVarInput(90, 190)}>
        <TwistStack
          spacing={toFluidVarInput(8, 8)}
          direction={{ min: 'column', lg: 'row' }}
          align={{ min: 'left', lg: 'center' }}
          justify="space-between"
        >
          <div class="job-info-container">
            <JobLocation isRemote={job.isRemote} location={job.location} />
            <div class="job-info">
              <div class="dot" />
              <p>
                {#if brandReskinEnabled}
                  <FontSizeDecorator size="caption">Req ID: {job.requisitionId}</FontSizeDecorator>
                {:else}
                  Req ID: {job.requisitionId}
                {/if}
              </p>
            </div>
            {#if formattedPublicationDate && job.location.endsWith('US')}
              <div class="job-info">
                <div class="dot" />
                <p>
                  {#if brandReskinEnabled}
                    <FontSizeDecorator size="caption">
                      Posted Date: {formattedPublicationDate}
                    </FontSizeDecorator>
                  {:else}
                    Posted Date: {formattedPublicationDate}
                  {/if}
                </p>
              </div>
            {/if}
          </div>
          {#if publishedDaysAgo !== undefined && publishedDaysAgo <= MAX_PUBLISHED_DAYS_AGO}
            <JobPublicationDate {publishedDaysAgo} />
          {/if}
        </TwistStack>
      </TwistBox>

      <TwistBox
        display={{ min: 'block', lg: 'none' }}
        marginTop={[
          ['min', 90],
          ['sm', 'lg', 90, 180]
        ]}
        marginBottom={[
          ['min', 48],
          ['sm', 'lg', 48, 96]
        ]}
      >
        <ApplyButton on:click={() => dispatch('apply-clicked')} />
      </TwistBox>
    </Container>
    {#if !brandReskinEnabled}
      <BUMarquee bu={job.bu} />
    {/if}
  </TwistBox>

  <img class="bu-floating-asset" src={`/images/careers/bu/floating/${job.bu}.webp`} alt={job.bu} />
</div>

<style lang="scss">
  @import 'src/scss/variables';
  @import 'src/scss/media';

  .hero {
    background: #000;
    position: relative;
    overflow: hidden;
    z-index: map-get($zIndex, careers-hero);
  }

  h1 {
    color: white;
    font-family: var(--font-family-primary);
    font-size: var(--h1-font-size);
    line-height: 105%;

    @include at('lg') {
      margin-right: var(--twist-core-spacing-4);
    }
  }

  .job-info-container {
    display: flex;
    gap: var(--gap);
    flex-direction: column;

    .job-info {
      display: flex;
      align-items: center;
      gap: var(--gap);

      .dot {
        display: none;
        width: 3px;
        height: 3px;
        background: var(--theme-job-location-color, var(--twist-color-gray-0));

        @include at('lg') {
          display: inline-block;
        }
      }

      p {
        color: var(--theme-job-location-color, var(--twist-color-gray-0));
        font-family: var(--font-family-primary);
        font-size: var(--id-font-size);
        font-weight: 500;
        line-height: 130%;
      }
    }

    @include at('lg') {
      flex-direction: row;
    }
  }

  .bu-logo {
    display: inline-block;
    height: var(--bu-logo-height);

    img {
      height: 100%;
      width: auto;
    }
  }

  .bu-floating-asset {
    position: absolute;
    width: var(--floating-asset-width);
    top: var(--floating-asset-top);
    right: calc(-1 * var(--floating-asset-right));

    @include at('lg') {
      top: calc(-1 * var(--floating-asset-top));
      right: var(--floating-asset-right);
    }
  }
</style>
